import React from "react";
import { LegalLayout } from "./LegalLayout";

const LAST_UPDATED = "8 February 2026";

/**
 * Cookie Policy — UK PECR / GDPR compliant.
 * Worthify uses:
 *   • Essential storage (always on, no consent under PECR)
 *   • PostHog product analytics + Sentry error tracking (opt-in via banner)
 */
export const CookiePolicyPage = ({ t, language = "en" }) => {
  if (language === "pl") {
    return (
      <LegalLayout title="Polityka cookies" lastUpdated={LAST_UPDATED} t={t} language={language}>
        <h2>1. Co to są cookies i localStorage</h2>
        <p>
          „Cookies" to małe pliki tekstowe zapisywane przez Twoją przeglądarkę. „localStorage" to podobny mechanizm przechowywania danych w przeglądarce, używany przez Worthify zamiast tradycyjnych cookies.
        </p>

        <h2>2. Jakich cookies / localStorage używamy</h2>

        <h3>Niezbędne (essential) — bez wymaganej zgody pod PECR</h3>
        <ul>
          <li>
            <code>pendingAnalysis</code> (localStorage) — przechowuje wyniki analizy podczas procesu płatności Stripe (aby po powrocie z checkoutu od razu zobaczyć dashboard). Czas: do zakończenia płatności lub zamknięcia karty.
          </li>
          <li>
            <code>pendingRegistration</code> / <code>pendingAskingPrice</code> / <code>pendingPdfReg</code> / <code>pendingPdfLang</code> / <code>pendingPdfPrice</code> (localStorage) — przechowują kontekst płatności podczas checkoutu Stripe.
          </li>
          <li>
            <code>worthifyPdfUnlockedReg</code> (localStorage) — zapisuje rejestrację, dla której zakupiłeś PDF premium, aby umożliwić ponowne pobranie. Czas: bezterminowo (możesz wyczyścić w ustawieniach przeglądarki).
          </li>
          <li>
            <code>worthify_cookie_consent</code> (localStorage) — zapisuje Twój wybór z banera cookies („tylko niezbędne" lub „z analityką").
          </li>
          <li>
            <strong>Stripe</strong> — Stripe Checkout (oddzielna domena <code>checkout.stripe.com</code>) ustawia własne cookies sesyjne podczas procesu płatności. Stripe jest oddzielnym administratorem danych — szczegóły w ich <a href="https://stripe.com/cookie-settings" target="_blank" rel="noopener noreferrer">polityce cookies</a>.
          </li>
        </ul>

        <h3>Analityka produktu — TYLKO za wyraźną zgodą</h3>
        <ul>
          <li>
            <strong>PostHog</strong> (<code>eu.i.posthog.com</code>) — produktowa analityka behawioralna w regionie EU/Frankfurt. Zapisujemy <strong>tylko zanonimizowane zdarzenia produktowe</strong>: wyświetlenia ekranów, kliknięcia w CTA, ukończenie lejka płatności. Adres IP użytkownika jest blokowany (<code>property_blacklist: ["$ip"]</code>). PostHog jest aktywowany TYLKO po kliknięciu „Akceptuję analitykę" w banerze cookies. W każdej chwili możesz wycofać zgodę przez wyczyszczenie localStorage.
          </li>
          <li>
            <strong>Sentry</strong> (<code>sentry.io</code>, region EU) — śledzenie błędów aplikacji w celu naprawy crashów. Sentry uruchamia się na podstawie uzasadnionego interesu (debugowanie błędów technicznych zgłaszanych przez Twoją przeglądarkę), ale w trybie privacy-first: zero PII, wszystkie pola tekstowe i formularze są maskowane (<code>maskAllText</code>, <code>maskAllInputs</code>, <code>blockAllMedia</code>). Adres IP nie jest zapisywany.
          </li>
        </ul>

        <h2>3. Czego NIE robimy</h2>
        <ul>
          <li>Nie używamy Google Analytics, Facebook Pixel, ani żadnych skryptów reklamowych</li>
          <li>Nie wysyłamy danych do reklamodawców</li>
          <li>Nie tworzymy profilu marketingowego ani nie sprzedajemy danych</li>
          <li>Nie udostępniamy Twojej aktywności stronom trzecim poza PostHog (na podstawie Twojej zgody) i Sentry (uzasadniony interes — debugowanie)</li>
        </ul>

        <h2>4. Jak zarządzać cookies i analityką</h2>
        <ul>
          <li><strong>Wycofać zgodę na analitykę</strong> — wyczyść <code>worthify_cookie_consent</code> w localStorage, baner pojawi się ponownie</li>
          <li><strong>Wyczyścić localStorage</strong> w ustawieniach przeglądarki (Chrome: Settings → Privacy → Site Data → worthify.co.uk → Delete)</li>
          <li><strong>Zablokować cookies Stripe</strong> — uniemożliwi to dokonanie płatności</li>
          <li><strong>Używać trybu incognito</strong> — wszystkie dane są usuwane po zamknięciu karty</li>
          <li><strong>Włączyć „Do Not Track"</strong> w przeglądarce — PostHog respektuje DNT i automatycznie wyłączy capturing</li>
        </ul>

        <h2>5. Zmiany</h2>
        <p>
          Aktualna wersja polityki dostępna na <code>worthify.co.uk/cookies</code>. Ostatnia aktualizacja: <strong>{LAST_UPDATED}</strong>.
        </p>

        <h2>6. Kontakt</h2>
        <p>
          Pytania: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>.
        </p>
      </LegalLayout>
    );
  }

  // English
  return (
    <LegalLayout title="Cookie Policy" lastUpdated={LAST_UPDATED} t={t} language={language}>
      <h2>1. About cookies and localStorage</h2>
      <p>
        "Cookies" are small text files saved by your browser. "localStorage" is a similar browser-storage mechanism used by Worthify instead of traditional cookies.
      </p>

      <h2>2. What we use</h2>

      <h3>Strictly necessary — no consent required under UK PECR</h3>
      <ul>
        <li>
          <code>pendingAnalysis</code> (localStorage) — stores analysis results during the Stripe payment process so the dashboard is ready when you return from checkout. Lifetime: until payment completes or tab closes.
        </li>
        <li>
          <code>pendingRegistration</code> / <code>pendingAskingPrice</code> / <code>pendingPdfReg</code> / <code>pendingPdfLang</code> / <code>pendingPdfPrice</code> (localStorage) — preserve the payment context during Stripe checkout.
        </li>
        <li>
          <code>worthifyPdfUnlockedReg</code> (localStorage) — saves the registration for which you purchased the Premium PDF, enabling re-downloads. Lifetime: persistent (you can clear via browser settings).
        </li>
        <li>
          <code>worthify_cookie_consent</code> (localStorage) — records your cookie-banner choice ("essential only" or "analytics accepted").
        </li>
        <li>
          <strong>Stripe</strong> — Stripe Checkout (separate domain <code>checkout.stripe.com</code>) sets its own session cookies during payment. Stripe is a separate data controller — see their <a href="https://stripe.com/cookie-settings" target="_blank" rel="noopener noreferrer">cookie policy</a> for details.
        </li>
      </ul>

      <h3>Product analytics — ONLY with explicit consent</h3>
      <ul>
        <li>
          <strong>PostHog</strong> (<code>eu.i.posthog.com</code>) — product behavioural analytics hosted in the EU/Frankfurt region. We record <strong>only anonymised product events</strong>: screen views, CTA clicks, payment funnel completion. The visitor's IP address is blocked (<code>property_blacklist: ["$ip"]</code>). PostHog is activated ONLY after you click "Accept analytics" in the cookie banner. You can withdraw consent at any time by clearing localStorage.
        </li>
        <li>
          <strong>Sentry</strong> (<code>sentry.io</code>, EU region) — application error tracking so we can fix crashes. Sentry runs under legitimate interest (debugging technical errors your browser reports), but in privacy-first mode: zero PII, all text fields and inputs are masked (<code>maskAllText</code>, <code>maskAllInputs</code>, <code>blockAllMedia</code>). IP addresses are not stored.
        </li>
      </ul>

      <h2>3. What we do NOT do</h2>
      <ul>
        <li>We do not use Google Analytics, Facebook Pixel, or any advertising scripts</li>
        <li>We do not send data to advertisers</li>
        <li>We do not build marketing profiles or sell data</li>
        <li>We do not share your activity with third parties beyond PostHog (under your consent) and Sentry (legitimate interest — debugging)</li>
      </ul>

      <h2>4. How to manage cookies and analytics</h2>
      <ul>
        <li><strong>Withdraw analytics consent</strong> — clear <code>worthify_cookie_consent</code> in localStorage, the banner will reappear</li>
        <li><strong>Clear localStorage</strong> in your browser settings (Chrome: Settings → Privacy → Site Data → worthify.co.uk → Delete)</li>
        <li><strong>Block Stripe cookies</strong> — this will prevent payments</li>
        <li><strong>Use incognito mode</strong> — all data is cleared when you close the tab</li>
        <li><strong>Enable "Do Not Track"</strong> in your browser — PostHog respects DNT and will automatically disable capturing</li>
      </ul>

      <h2>5. Changes</h2>
      <p>
        The current policy is at <code>worthify.co.uk/cookies</code>. Last updated: <strong>{LAST_UPDATED}</strong>.
      </p>

      <h2>6. Contact</h2>
      <p>
        Questions: <a href="mailto:support@worthify.co.uk">support@worthify.co.uk</a>.
      </p>
    </LegalLayout>
  );
};